import { useState, useEffect, useCallback } from "react";
import { BlogPost, defaultBlogPosts } from "@/mocks/blog";

const STORAGE_KEY = "quiet_recovery_blog_posts";

function loadPosts(): BlogPost[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // Corrupt storage, fall through to defaults
  }
  const seeded = [...defaultBlogPosts];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
  return seeded;
}

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim()
    .replace(/^-|-$/g, "");
}

function generateId(): string {
  return `post-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setPosts(loadPosts());
    setLoaded(true);
  }, []);

  const save = useCallback((updated: BlogPost[]) => {
    setPosts(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, []);

  const getPostBySlug = useCallback(
    (slug: string): BlogPost | undefined => {
      return posts.find((p) => p.slug === slug);
    },
    [posts],
  );

  const getPostById = useCallback(
    (id: string): BlogPost | undefined => {
      return posts.find((p) => p.id === id);
    },
    [posts],
  );

  const publishedPosts = posts.filter((p) => p.published);

  const createPost = useCallback(
    (data: Partial<BlogPost> & { title: string; content: string }): BlogPost => {
      const now = new Date().toISOString();
      const newPost: BlogPost = {
        id: generateId(),
        slug: generateSlug(data.title),
        title: data.title,
        excerpt: data.excerpt || "",
        content: data.content,
        coverImage: data.coverImage || "",
        category: data.category || "Uncategorized",
        tags: data.tags || [],
        published: data.published ?? false,
        createdAt: now,
        updatedAt: now,
      };
      const updated = [...posts, newPost];
      save(updated);
      return newPost;
    },
    [posts, save],
  );

  const updatePost = useCallback(
    (id: string, data: Partial<BlogPost>): BlogPost | undefined => {
      const index = posts.findIndex((p) => p.id === id);
      if (index === -1) return undefined;
      const updatedPost: BlogPost = {
        ...posts[index],
        ...data,
        slug: data.title ? generateSlug(data.title) : posts[index].slug,
        updatedAt: new Date().toISOString(),
      };
      const updated = [...posts];
      updated[index] = updatedPost;
      save(updated);
      return updatedPost;
    },
    [posts, save],
  );

  const deletePost = useCallback(
    (id: string): boolean => {
      const updated = posts.filter((p) => p.id !== id);
      if (updated.length === posts.length) return false;
      save(updated);
      return true;
    },
    [posts, save],
  );

  const resetToDefaults = useCallback(() => {
    save([...defaultBlogPosts]);
  }, [save]);

  return {
    posts,
    publishedPosts,
    loaded,
    getPostBySlug,
    getPostById,
    createPost,
    updatePost,
    deletePost,
    resetToDefaults,
  };
}